import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Pencil, Check, X, Loader2 } from "lucide-react";

/**
 * Compact inline slug editor for institutions/campuses.
 * Only visible to super_admin.
 *
 *   <PortalSlugEditor kind="institution" id={i.id} value={i.slug} onSaved={refetch} />
 *   <PortalSlugEditor kind="campus"      id={c.id} value={c.slug} onSaved={refetch} />
 */
export function PortalSlugEditor({ kind, id, value, onSaved }) {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  if (!isSuper) return null;

  const save = async () => {
    const slug = draft.trim().toLowerCase();
    if (!slug) return;
    setSaving(true);
    try {
      const path = kind === "institution"
        ? `/institutions/${id}/slug`
        : `/campuses/${id}/slug`;
      await api.patch(path, { slug });
      toast.success(`Portal slug set to "${slug}"`);
      setEditing(false);
      onSaved?.(slug);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save slug";
      toast.error(typeof detail === "string" ? detail : "Could not save slug");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); setDraft(value || ""); }}
        className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
        title="Portal subdomain slug"
        data-testid={`portal-slug-${kind}-${id}`}
      >
        <Globe className="h-3 w-3" />
        {value || "set slug"}
        <Pencil className="h-2.5 w-2.5 opacity-60" />
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value.toLowerCase())}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        placeholder="stpeters"
        className="text-[11px] font-mono px-2 py-0.5 rounded-md border border-indigo-300 outline-none focus:ring-1 focus:ring-indigo-400 w-28"
        data-testid={`portal-slug-input-${kind}-${id}`}
      />
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="h-5 w-5 rounded-md bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 disabled:opacity-50"
        data-testid={`portal-slug-save-${kind}-${id}`}
        title="Save"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="h-5 w-5 rounded-md bg-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-300"
        title="Cancel"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
